import { FormatService, FormatData } from "./FormatService";
import {
    RootState,
    Kontaktuppgifter,
    Anonym,
    VilkenRoll,
    VetPersonnummer,
} from "@/store/types";
import { AnonymText } from "@/store/map";
import { MatrixServiceImpl, MatrixServiceId } from "../MatrixService";
import { VISA_BARA_TEXTNYCKEL } from "@/constants";

export class KontaktuppgifterFormatService extends FormatService {
    private getAnonymValue({ villDuVaraAnonym }: Kontaktuppgifter): string {
        if (villDuVaraAnonym) {
            return this.$t(AnonymText[villDuVaraAnonym]);
        }
        return "";
    }

    private isAnonymNej(state: RootState): boolean {
        return (
            MatrixServiceImpl.isEnabled(MatrixServiceId.ANONYM, state) &&
            state.kontaktuppgifter.villDuVaraAnonym === Anonym.NEJ
        );
    }

    public getHeader(): string {
        return `@s@${this.$t(
            "impulskontrollutredning.anmalan.alla.misstanke.generell.privat.rubrik.kontaktuppgifter",
        )}@!s@`;
    }

    public getFormData(state: RootState): Array<FormatData> {
        const textContext =
            state.forsteg.vilkenroll === VilkenRoll.MYNDIGHET
                ? "myndighet"
                : "privat";
        return [
            // Privatperson anmälare
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.etikett.anonym",
                value: this.getAnonymValue(state.kontaktuppgifter),
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANONYM,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.anmalare.etikett.dittfornamn",
                value: state.kontaktuppgifter.anmalareDittFornamn,
                isEnabled: this.isAnonymNej(state),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.anmalare.etikett.dittefternamn",
                value: state.kontaktuppgifter.anmalareDittEfternamn,
                isEnabled: this.isAnonymNej(state),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.anmalare.etikett.kontaktuppgifter",
                value: state.kontaktuppgifter.anmalareKontaktuppgift,
                isEnabled: this.isAnonymNej(state),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.anmalare.etikett.vilkenrelation",
                value: state.kontaktuppgifter.anmalareRelationTill,
                isEnabled: this.isAnonymNej(state),
            },
            // Myndighet anmälare
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmalare.etikett.varjobbar",
                value: state.kontaktuppgifter.anmalareVarJobbar,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.JOBB,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmalare.etikett.dittfornamn",
                value: state.kontaktuppgifter.anmalareDittFornamn,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.FORNAMN,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmalare.etikett.dittefternamn",
                value: state.kontaktuppgifter.anmalareDittEfternamn,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.EFTERNAMN,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmalare.etikett.dintelefon",
                value: state.kontaktuppgifter.anmalareDinTelefon,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.TELEFONNUMMER,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmalare.etikett.kontaktuppgift",
                value: state.kontaktuppgifter.anmalareOvrigKontaktuppgift,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.OVRIG_KONTAKT,
                    state,
                ),
            },
            // Anmäld privatperson
            {
                textnyckel: `impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmald.rubrik.anmald`,
                value: VISA_BARA_TEXTNYCKEL,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_PERSON,
                    state,
                ),
            },
            {
                textnyckel: `impulskontrollutredning.anmalan.${textContext}.kontaktuppgifter.privat.anmald.etikett.fornamn`,
                value: state.kontaktuppgifter.anmaldFornamn,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_PERSON,
                    state,
                ),
            },
            {
                textnyckel: `impulskontrollutredning.anmalan.${textContext}.kontaktuppgifter.privat.anmald.etikett.efternamn`,
                value: state.kontaktuppgifter.anmaldEfternamn,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_PERSON,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmald.etikett.personnummer",
                value: state.kontaktuppgifter.anmaldPersonnummer,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.PERSON_PERSON_NR,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmald.etikett.kontaktuppgift",
                value: state.kontaktuppgifter.anmaldKontaktuppgift,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.PERSON_KONTAKT,
                    state,
                ),
            },
            // Vet personnummer ja
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.anmald.etikett.personnummer",
                value: state.kontaktuppgifter.anmaldPersonnummer,
                isEnabled:
                    state.kontaktuppgifter.vetPersonnummer ===
                    VetPersonnummer.JA,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.myndighet.kontaktuppgifter.privat.anmald.etikett.kontaktuppgift",
                value: state.kontaktuppgifter.anmaldKontaktuppgift,
                isEnabled:
                    state.kontaktuppgifter.vetPersonnummer ===
                    VetPersonnummer.JA,
            },
            // Vet personnummer nej
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.etikett.fodelsedatumelleralder",
                value: state.kontaktuppgifter.anmaldFodelsedatum,
                isEnabled:
                    state.kontaktuppgifter.vetPersonnummer ===
                    VetPersonnummer.NEJ,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.etikett.adressellerort",
                value: state.kontaktuppgifter.anmaldAdressOrt,
                isEnabled:
                    state.kontaktuppgifter.vetPersonnummer ===
                    VetPersonnummer.NEJ,
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.privat.kontaktuppgifter.privat.etikett.andrauppgifter",
                value: state.kontaktuppgifter.anmaldAndraUppgifter,
                isEnabled:
                    state.kontaktuppgifter.vetPersonnummer ===
                    VetPersonnummer.NEJ,
            },
            // Anmält företag
            {
                textnyckel: `impulskontrollutredning.anmalan.alla.kontaktuppgifter.foretag.anmald.rubrik.anmaldforetag`,
                value: VISA_BARA_TEXTNYCKEL,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_FORETAG,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.kontaktuppgifter.foretag.anmald.etikett.foretag",
                value: state.kontaktuppgifter.foretagNamn,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_FORETAG,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.kontaktuppgifter.foretag.anmald.etikett.organisationsnummer",
                value: state.kontaktuppgifter.organisationsnummer,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_FORETAG,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.kontaktuppgifter.foretag.anmald.etikett.foretradare",
                value: state.kontaktuppgifter.foretagForetradare,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_FORETAG,
                    state,
                ),
            },
            {
                textnyckel:
                    "impulskontrollutredning.anmalan.alla.kontaktuppgifter.foretag.anmald.etikett.ovrigauppgifter",
                value: state.kontaktuppgifter.foretagOvrigaUppgifter,
                isEnabled: MatrixServiceImpl.isEnabled(
                    MatrixServiceId.ANMALA_FORETAG,
                    state,
                ),
            },
        ];
    }
}
